import {
  searchMove,
  type GameContext,
  type GameState,
  type SearchOptions,
  type SearchResult,
} from '@archon3d/core';
import type { BrainAnswer, BrainRequest } from './aiWorker';

/** The AI's move, and whether it is only the quick one-ply answer given when thinking failed. */
export type BrainResult = SearchResult & { quick?: boolean };

/**
 * Chooses the AI's move. May answer at once (tests, fallbacks) or later (a worker). A brain that
 * thinks elsewhere can `cancel` the thinks in flight: their promises reject, and never answer.
 */
export type Brain = ((
  ctx: GameContext,
  state: GameState,
  options: Omit<SearchOptions, 'now'>,
) => BrainResult | Promise<BrainResult>) & { cancel?(): void };

/** The search on the calling thread, answering synchronously. */
export const localBrain: Brain = (ctx, state, options) =>
  searchMove(ctx, state, { ...options, now: () => performance.now() });

/** How long the page's own thread may think when no worker can: a moment, never a frozen frame. */
export const QUICK_THINK_MS = 150;

/**
 * What the AI plays when its worker cannot think: a short look on the page's own thread, two plies
 * at most and a moment's clock, where the full think would freeze the page (and the headset).
 */
export function quickMove(
  ctx: GameContext,
  state: GameState,
  options: Omit<SearchOptions, 'now'>,
): BrainResult {
  const { softLimitMs: _target, minDepth: _own, ...rest } = options;
  const result = searchMove(ctx, state, {
    ...rest,
    depth: Math.min(rest.depth, 2),
    timeLimitMs: QUICK_THINK_MS,
    now: () => performance.now(),
  });
  return { ...result, quick: true };
}

/** A think given up on: the state moved on, or the session went away. */
export class CancelledThink extends Error {
  constructor() {
    super('The AI stopped thinking: the game moved on');
    this.name = 'CancelledThink';
  }
}

interface Waiting {
  resolve(result: BrainResult): void;
  reject(error: unknown): void;
  quick(): BrainResult;
}

/**
 * The search in a Web Worker so a deep think never stalls rendering or the headset; one request
 * at a time. A think given up on stops the worker, which the next think starts afresh. If the
 * worker fails, the waiting think gets the quick move, never the full search on this thread; and
 * where no worker can run at all (none exists, or it failed before it ever answered), every think
 * is the quick move.
 */
export function createWorkerBrain(): Brain & { cancel(): void; dispose(): void } {
  let worker: Worker | null = null;
  let nextId = 1;
  /** Each game's number for the worker, which keeps the game it is told about under it. */
  const contextIds = new WeakMap<GameContext, number>();
  let nextContext = 1;
  /** The games the running worker has been told about: a worker started afresh knows none. */
  let known = new Set<number>();
  const pending = new Map<number, Waiting>();
  /** A worker has answered in this page, so a later failure is that think's, not the page's. */
  let answered = false;
  let broken = false;
  const quickly = (waiting: Waiting) => {
    try {
      waiting.resolve(waiting.quick());
    } catch (error) {
      waiting.reject(error);
    }
  };
  const stop = () => {
    worker?.terminate();
    worker = null;
  };
  const settleAll = (settle: (waiting: Waiting) => void) => {
    const waiting = [...pending.values()];
    pending.clear();
    for (const one of waiting) settle(one);
  };
  const start = () => {
    if (worker || broken || typeof Worker === 'undefined') return worker;
    try {
      worker = new Worker(new URL('./aiWorker.ts', import.meta.url), { type: 'module' });
    } catch {
      return null;
    }
    known = new Set();
    worker.onmessage = (event: MessageEvent<BrainAnswer>) => {
      answered = true;
      const waiting = pending.get(event.data.id);
      if (!waiting) return;
      pending.delete(event.data.id);
      if ('error' in event.data) quickly(waiting);
      else waiting.resolve(event.data.result);
    };
    worker.onerror = () => {
      if (!answered) {
        // It never loaded: starting another for every move would only fail the same way.
        broken = true;
        console.warn('The AI worker could not start; the AI makes quick moves on the page.');
      }
      stop();
      settleAll(quickly);
    };
    return worker;
  };
  const brain = ((ctx, state, options) => {
    const w = start();
    if (!w) return quickMove(ctx, state, options);
    return new Promise<BrainResult>((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve, reject, quick: () => quickMove(ctx, state, options) });
      let context = contextIds.get(ctx);
      if (context === undefined) {
        context = nextContext++;
        contextIds.set(ctx, context);
      }
      const request: BrainRequest = {
        id,
        context,
        setup: known.has(context)
          ? undefined
          : {
              board: ctx.board.def,
              roster: ctx.roster,
              definition: ctx.definition,
              combatMode: ctx.combatMode,
              cardLevels: ctx.cardLevels,
            },
        state,
        options,
      };
      known.add(context);
      w.postMessage(request);
    });
  }) as Brain;
  const giveUp = () => {
    stop();
    settleAll((waiting) => waiting.reject(new CancelledThink()));
  };
  return Object.assign(brain, {
    cancel() {
      // A worker cannot be interrupted mid-search: it is stopped, and started again when needed.
      if (pending.size > 0) giveUp();
    },
    dispose: giveUp,
  });
}
